import { type Compartment, EditorState } from '@codemirror/state'
import { EditorView } from '@codemirror/view'
import type { MutableRefObject } from 'react'

import type { ParsedTemplateVariable } from './template-variable.interface'
import { createEditorExtensions } from './template-variable-editor-extensions.config'
import { parseTemplateVariables } from './template-variable-parser.util'

interface MountEditorParams {
  container: HTMLDivElement
  value: string
  placeholder: string
  minHeight: string
  isDisabled: boolean
  editableCompartment: Compartment
  viewRef: MutableRefObject<EditorView | null>
  lastExternalValueRef: MutableRefObject<string>
  onDocChange: (doc: string) => void
  onBlur: () => void
  onVariablesChange: ((variableList: ParsedTemplateVariable[]) => void) | undefined
}

/**
 * Creates and mounts a CodeMirror EditorView into the given container.
 * Returns a cleanup function that destroys the view.
 */
export const mountEditor = (params: MountEditorParams): (() => void) => {
  const updateListener = EditorView.updateListener.of((update) => {
    if (update.docChanged) {
      const newDoc = update.state.doc.toString()
      params.lastExternalValueRef.current = newDoc
      params.onDocChange(newDoc)
    }
    if (update.focusChanged && !update.view.hasFocus) {
      params.onBlur()
    }
  })

  const extensions = createEditorExtensions({
    placeholder: params.placeholder,
    minHeight: params.minHeight,
    isDisabled: params.isDisabled,
    editableCompartment: params.editableCompartment,
    updateListener,
  })

  const state = EditorState.create({ doc: params.value, extensions })
  const view = new EditorView({ state, parent: params.container })
  params.viewRef.current = view

  if (params.value) {
    params.onVariablesChange?.(parseTemplateVariables(params.value))
  }

  return () => {
    view.destroy()
    params.viewRef.current = null
  }
}
